import { skipToken, useQuery, type QueryClient } from '@tanstack/react-query';
import { isApiError, type ApiError } from '../../api';
import { replyWindowRefusalKey } from './keys';

/** Código com que o servidor recusa (409) um envio ou reenvio depois que a janela de resposta fechou. */
export const REPLY_WINDOW_CLOSED_CODE = 'reply_window_closed';

/** Texto de reserva quando o servidor recusa sem mensagem legível. */
export const REPLY_WINDOW_REFUSED_FALLBACK =
  'A janela de resposta está encerrada: só dá para responder depois que o cliente escrever de novo.';

/** O servidor diz que a janela fechou (409 `reply_window_closed`)? Nada foi gravado nem enviado. */
export const isReplyWindowClosed = (error: unknown): error is ApiError =>
  isApiError(error) && error.status === 409 && error.code === REPLY_WINDOW_CLOSED_CODE;

/** A explicação a mostrar: a mensagem do servidor (já em português) ou, sem ela, a de reserva. */
export const refusalText = (error: ApiError): string =>
  error.message.trim() || REPLY_WINDOW_REFUSED_FALLBACK;

export interface WindowRefusal {
  /** Explicação em português, a do servidor (ou a de reserva). */
  message: string;
  /**
   * O texto que a pessoa mandou voltou para o rascunho (envio novo). Falso no reenvio de uma
   * mensagem que já existe: não há texto a devolver.
   */
  draftKept: boolean;
  /** Instante da recusa (ms): distingue uma recusa nova da anterior e serve para anunciá-la uma vez. */
  at: number;
}

export function recordWindowRefusal(
  queryClient: QueryClient,
  conversationId: string,
  refusal: Omit<WindowRefusal, 'at'>,
): void {
  queryClient.setQueryData<WindowRefusal>(replyWindowRefusalKey(conversationId), {
    ...refusal,
    at: Date.now(),
  });
}

/** A recusa mais recente do servidor nesta conversa, ou `null`. */
export function useWindowRefusal(conversationId: string): WindowRefusal | null {
  const { data } = useQuery<WindowRefusal | null>({
    queryKey: replyWindowRefusalKey(conversationId),
    queryFn: skipToken,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? null;
}
